import { directionKey, featureKey, type Lesson, type Step } from '@vrlearning/lesson-schema';
import type { AssemblyResult } from './assembly.js';

/**
 * What the student is being asked for right now.
 *
 * The prototypes had two unrelated shapes for this: the VR tour hid its Next
 * button while a quiz was open, and the AR lesson ran a separate
 * intro/estimate/measure/result state machine. They are the same idea, so v1
 * has one phase model and every interaction is a phase of a step.
 *
 * A predict step with a panel opens on 'intro': the panel is what to look at
 * before guessing ("Kijk eerst goed … Kies je schatting"), so it comes before
 * the question, and the reveal after it -- the task, or the next step
 * (docs/04-lesson-format.md, docs/10-pedagogy.md).
 */
export type StepPhase = 'intro' | 'panel' | 'quiz' | 'predict' | 'task' | 'result';

export type PlayerState = {
  readonly index: number;
  readonly step: Step;
  readonly phase: StepPhase;
  readonly canGoBack: boolean;
  readonly canGoForward: boolean;
  /** The option chosen on this step's predict, once it has been chosen. */
  readonly predicted?: number;
};

/** Matches the payloads in docs/03-data-model.md. */
export type LessonResult =
  | { stepId: string; kind: 'quiz'; payload: { choice: number; correct: boolean } }
  | {
      stepId: string;
      kind: 'measure';
      payload: { estimate: number; measuredMeters: number; correct: boolean };
    }
  | { stepId: string; kind: 'hotspot'; payload: { hotspot: string } }
  | { stepId: string; kind: 'assemble'; payload: AssemblyResult };

export type QuizOutcome = { choice: number; correct: boolean; correctIndex: number };
export type MeasureOutcome = {
  estimate: number;
  actualIndex: number;
  measuredMeters: number;
  correct: boolean;
};

export type PlayerOptions = {
  /** Called once per recorded result. In M3 this feeds the WebSocket queue. */
  onResult?: (result: LessonResult) => void;
};

/** Per-step progress, kept so revisiting a step does not re-ask or double count. */
type StepProgress = {
  phase: StepPhase;
  quizChoice?: number;
  estimate?: number;
  openedHotspots: Set<string>;
};

/**
 * Which bucket a measurement falls into, given ascending boundaries.
 * `edges: [1, 2]` means: below 1 -> 0, 1 up to 2 -> 1, 2 and above -> 2.
 */
export function bucketFor(meters: number, edges: readonly number[]): number {
  let index = 0;
  while (index < edges.length && meters >= edges[index]!) index++;
  return index;
}

/**
 * Who chooses the step: the student, or the teacher steering the class
 * (docs/05-session-protocol.md). 'free' when no session is involved at all,
 * which is the desktop preview and a headset practising on its own.
 */
export type StepControl = 'locked' | 'free';

export class LessonPlayer {
  readonly lesson: Lesson;

  #control: StepControl = 'free';
  #index = 0;
  #progress: StepProgress[];
  #listeners = new Set<(state: PlayerState) => void>();
  #onResult: ((result: LessonResult) => void) | undefined;

  constructor(lesson: Lesson, options: PlayerOptions = {}) {
    this.lesson = lesson;
    this.#onResult = options.onResult;
    this.#progress = lesson.steps.map((step) => ({
      phase: initialPhase(step),
      openedHotspots: new Set<string>(),
    }));
  }

  get state(): PlayerState {
    const step = this.lesson.steps[this.#index]!;
    const phase = this.#here.phase;
    // While a question is open the student must commit to an answer before
    // moving on: retrieval only works if they actually choose. A predict
    // step's introduction leads to its question, never past it; going back
    // from it is as free as from any panel.
    const settled = phase === 'panel' || phase === 'result';
    const estimate = this.#here.estimate;
    return {
      index: this.#index,
      step,
      phase,
      // No way forward in a locked class, so the lectern shows no button that
      // the server would refuse. Without this a student pressing Verder moves
      // on their own, the progress report is rejected, nothing is sent back,
      // and that headset sits on the wrong step until the teacher happens to
      // move the class again.
      canGoBack: (settled || phase === 'intro') && this.#index > 0 && this.#control === 'free',
      canGoForward:
        settled && this.#index < this.lesson.steps.length - 1 && this.#control === 'free',
      ...(step.predict && estimate !== undefined ? { predicted: estimate } : {}),
    };
  }

  /** Who steers: the pupil (`free`) or the teacher (`locked`). */
  get control(): StepControl {
    return this.#control;
  }

  get #here(): StepProgress {
    return this.#progress[this.#index]!;
  }

  onChange(listener: (state: PlayerState) => void): () => void {
    this.#listeners.add(listener);
    return () => this.#listeners.delete(listener);
  }

  #changed(): void {
    const state = this.state;
    for (const listener of this.#listeners) listener(state);
  }

  #record(result: LessonResult): void {
    this.#onResult?.(result);
  }

  /**
   * Hands the steering to the teacher, or takes it back.
   *
   * Notifies, because the buttons on the lectern have to appear and disappear
   * with it -- a stale Verder button is exactly the thing this prevents.
   */
  setControl(control: StepControl): void {
    if (this.#control === control) return;
    this.#control = control;
    this.#changed();
  }

  next(): void {
    if (!this.state.canGoForward) return;
    this.#leave();
    this.#index++;
    this.#changed();
  }

  previous(): void {
    if (!this.state.canGoBack) return;
    this.#leave();
    this.#index--;
    this.#changed();
  }

  /**
   * A question left unanswered starts at its introduction when the step is
   * entered again -- by a teacher's jump, most likely. The question on its
   * own reads as coming from nowhere.
   */
  #leave(): void {
    if (this.#here.phase === 'predict') this.#here.phase = initialPhase(this.state.step);
  }

  /** Used by the teacher console in M3, and by the desktop preview now. */
  goto(stepId: string): void {
    const index = this.lesson.steps.findIndex((step) => step.id === stepId);
    if (index < 0 || index === this.#index) return;
    this.#leave();
    this.#index = index;
    this.#changed();
  }

  answerQuiz(choice: number): QuizOutcome | undefined {
    const quiz = this.state.step.quiz;
    if (!quiz || this.#here.phase !== 'quiz') return undefined;

    const correct = choice === quiz.answer;
    this.#here.quizChoice = choice;
    this.#here.phase = 'result';
    this.#record({
      stepId: this.state.step.id,
      kind: 'quiz',
      payload: { choice, correct },
    });
    this.#changed();
    return { choice, correct, correctIndex: quiz.answer };
  }

  /**
   * From a predict step's introduction to its question. Within the step, so
   * a pupil in a class the teacher steers can do it too.
   */
  beginPredict(): void {
    if (this.#here.phase !== 'intro') return;
    this.#here.phase = 'predict';
    this.#changed();
  }

  answerPredict(choice: number): void {
    const step = this.state.step;
    if (!step.predict || this.#here.phase !== 'predict') return;

    this.#here.estimate = choice;
    this.#here.phase = step.task ? 'task' : 'result';
    this.#changed();
  }

  completeTask(measuredMeters: number): MeasureOutcome {
    const step = this.state.step;
    const task = step.task;
    if (!task) throw new Error(`step "${step.id}" has no task`);
    // `task` is a union since ADR-025 added assembling, and only a measure
    // task has edges. Refused rather than coerced: a caller measuring an
    // assemble step has confused two interactions, and quietly bucketing the
    // number against no edges would hide that until a teacher read the
    // results.
    if (task.type !== 'measure') {
      throw new Error(`step "${step.id}" is a "${task.type}" task, not something to measure`);
    }

    const estimate = this.#here.estimate;
    if (estimate === undefined) {
      throw new Error(`step "${step.id}" needs an estimate before a measurement`);
    }

    // Millimetres: the hit-test is not accurate beyond that, and a raw float
    // would imply a precision the headset does not have.
    const measured = Math.round(measuredMeters * 1000) / 1000;
    const actualIndex = bucketFor(measured, task.edges ?? []);
    const correct = actualIndex === estimate;

    this.#here.phase = 'result';
    this.#record({
      stepId: step.id,
      kind: 'measure',
      payload: { estimate, measuredMeters: measured, correct },
    });
    this.#changed();
    return { estimate, actualIndex, measuredMeters: measured, correct };
  }

  /**
   * The last part is in (ADR-025).
   *
   * Recorded once: coming back to a skeleton already built must not report it
   * built twice, which would read in the results as a second student. The
   * assembly itself -- which bone is where -- lives in the renderer, because
   * it is about positions in a room; this only hears that it is finished.
   */
  completeAssembly(result: AssemblyResult): void {
    const step = this.state.step;
    if (step.task?.type !== 'assemble' || this.#here.phase !== 'task') return;

    this.#here.phase = 'result';
    this.#record({ stepId: step.id, kind: 'assemble', payload: result });
    this.#changed();
  }

  /** Measure the same thing again; the estimate stands, a new result is sent. */
  retryTask(): void {
    if (!this.state.step.task || this.#here.estimate === undefined) return;
    this.#here.phase = 'task';
    this.#changed();
  }

  /**
   * Returns the hotspot to display, or undefined when the object has none on
   * this step. Only the first opening is reported: the metric is engagement,
   * not how often a ray happened to cross the badge.
   *
   * `key` is an object's id, or a direction hotspot's key in a 360° photo
   * (ADR-036): `richting-<n>`, its place in the step's hotspots from 1
   * (lesson-schema's directionKey), or a world feature's, `wereld:<feature>`
   * (ADR-087, lesson-schema's featureKey). A `find` not yet pointed is never
   * shown, so never opened. An object or a feature with several hotspots on the
   * step is paged: `page` picks one (wrapping), and each is recorded once as
   * `<object>#<n>` or `wereld:<feature>#<n>`.
   */
  openHotspot(key: string, page = 0): OpenedHotspot | undefined {
    const step = this.state.step;
    const all = step.hotspots ?? [];
    const matches = all.filter(
      (h, index) =>
        (h.object !== undefined && h.object === key) ||
        (h.toward !== undefined && directionKey(index) === key) ||
        (h.feature !== undefined && featureKey(h.feature) === key),
    );
    const pages = matches.length;
    if (pages === 0) return undefined;

    // A page past the end wraps round to the first.
    const at = ((Math.trunc(page) % pages) + pages) % pages;
    const hotspot = matches[at]!;
    const resultKey = pages > 1 ? `${key}#${at + 1}` : key;

    if (!this.#here.openedHotspots.has(resultKey)) {
      this.#here.openedHotspots.add(resultKey);
      this.#record({ stepId: step.id, kind: 'hotspot', payload: { hotspot: resultKey } });
    }
    return { hotspot, page: at, pages };
  }
}

type Hotspot = NonNullable<Step['hotspots']>[number];

/** A hotspot as opened: which of the object's hotspots (from 0) out of how many. */
export type OpenedHotspot = { hotspot: Hotspot; page: number; pages: number };

function initialPhase(step: Step): StepPhase {
  if (step.predict) return step.panel ? 'intro' : 'predict';
  if (step.quiz) return 'quiz';
  // An assembly has nothing to predict, and the only other way into 'task'
  // was answering one -- so it used to start as a finished panel and never
  // ask for a single bone. A measure step still waits for its estimate.
  if (step.task?.type === 'assemble') return 'task';
  return 'panel';
}
